import { getApiErrorMessage } from '../utils';
import { Button } from '@majidalfuttaim/maf-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { resendDgcActivationLink } from 'api/requests/dgc/dgcOrders';
import Spinner2 from 'shared/components/Spinner2';
import { toast } from 'shared/hooks/useToast';
import { queryKeyBuilder } from 'shared/query';
import { Tooltip } from 'tremor/components/Tooltip';

const RESEND_ERROR_FALLBACK = 'Could not resend this activation link. Please try again.';

type ResendActivationLinkButtonProps = {
  linkUuid: string;
  orderUuid: string;
  blockedReason: string | null;
};

function ResendActivationLinkButton({ linkUuid, orderUuid, blockedReason }: ResendActivationLinkButtonProps) {
  const queryClient = useQueryClient();

  const resendMutation = useMutation({
    mutationFn: () => resendDgcActivationLink(linkUuid),
    onSuccess: () => {
      toast({ title: 'Activation link resent.', variant: 'success' });
      queryClient.invalidateQueries({ queryKey: queryKeyBuilder.dgcOrderDetail(orderUuid) });
    },
    onError: (error) => {
      toast({ title: getApiErrorMessage(error, RESEND_ERROR_FALLBACK), variant: 'error' });
    },
  });

  const resendButton = (
    <Button
      variant="outline"
      rounded={true}
      disabled={!!blockedReason || resendMutation.isPending}
      onClick={() => resendMutation.mutate()}
    >
      <div className="flex gap-2 items-center">
        {resendMutation.isPending && <Spinner2 className="w-4 h-4" />}
        <span>Resend activation link</span>
      </div>
    </Button>
  );

  // Disabled buttons don't receive pointer events, so the tooltip trigger wraps it in a span instead.
  return blockedReason ? (
    <Tooltip asChild content={blockedReason}>
      <span>{resendButton}</span>
    </Tooltip>
  ) : (
    resendButton
  );
}

export default ResendActivationLinkButton;
